<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.application_queue.title') }}
            </h2>
            <p class="text-gray-600">
              {{ $t('government.application_queue.subtitle') }}
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Application Queue Content -->
      <main id="application-queue-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Filter Controls -->
        <div
          id="filter-section"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex flex-wrap items-center gap-4">
            <div class="flex items-center space-x-2">
              <label class="text-sm font-medium text-dark-navy"
                >{{ $t('government.application_queue.filters.application_type') }}</label
              >
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>{{ $t('government.application_queue.filters.all_types') }}</option>
                <option>{{ $t('government.application_queue.filters.pin') }}</option>
                <option>{{ $t('government.application_queue.filters.pdd') }}</option>
                <option>{{ $t('government.application_queue.filters.authorization') }}</option>
              </select>
            </div>
            <div class="flex items-center space-x-2">
              <label class="text-sm font-medium text-dark-navy">{{ $t('government.application_queue.filters.status') }}</label>
              <select
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              >
                <option>{{ $t('government.application_queue.filters.all_status') }}</option>
                <option>{{ $t('government.application_queue.filters.pending_review') }}</option>
                <option>{{ $t('government.application_queue.filters.under_review') }}</option>
                <option>{{ $t('government.application_queue.filters.approved') }}</option>
                <option>{{ $t('government.application_queue.filters.rejected') }}</option>
              </select>
            </div>
            <div class="flex items-center space-x-2">
              <label class="text-sm font-medium text-dark-navy"
                >{{ $t('government.application_queue.filters.date_range') }}</label
              >
              <input
                type="date"
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              />
              <span class="text-sm text-gray-500">{{ $t('government.application_queue.filters.to') }}</span>
              <input
                type="date"
                class="px-3 py-2 border border-light-gray rounded-md text-sm"
              />
            </div>
            <button
              class="px-4 py-2 bg-forest-green text-white rounded-md text-sm hover:bg-green-700"
            >
              <i class="fa-solid fa-search mr-2"></i>{{ $t('government.application_queue.filters.filter') }}
            </button>
          </div>
        </div>

        <!-- Summary Cards -->
        <div
          id="summary-cards"
          class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        >
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.application_queue.stats.total_applications') }}</p>
                <p class="text-2xl font-medium text-dark-navy">47</p>
              </div>
              <i class="fa-solid fa-file-alt text-forest-green text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.application_queue.stats.pin_applications') }}</p>
                <p class="text-2xl font-medium text-electric-blue">18</p>
              </div>
              <i class="fa-solid fa-info-circle text-electric-blue text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.application_queue.stats.pdd_applications') }}</p>
                <p class="text-2xl font-medium text-forest-green">16</p>
              </div>
              <i
                class="fa-solid fa-file-contract text-forest-green text-xl"
              ></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.application_queue.stats.authorizations') }}</p>
                <p class="text-2xl font-medium text-dark-navy">13</p>
              </div>
              <i class="fa-solid fa-check-circle text-dark-navy text-xl"></i>
            </div>
          </div>
        </div>

        <!-- Applications Table -->
        <div
          id="applications-table"
          class="bg-white rounded-md border border-light-gray"
        >
          <div class="p-5 border-b border-light-gray">
            <div class="flex justify-between items-center">
              <h3 class="text-lg font-medium text-dark-navy">
                {{ $t('government.application_queue.table.title') }}
              </h3>
              <div class="flex space-x-2">
                <button
                  class="px-3 py-2 text-sm border border-light-gray rounded-md hover:bg-gray-50"
                >
                  <i class="fa-solid fa-download mr-2"></i>{{ $t('government.application_queue.table.export') }}
                </button>
                <button
                  class="px-3 py-2 text-sm bg-forest-green text-white rounded-md hover:bg-green-700"
                >
                  <i class="fa-solid fa-plus mr-2"></i>{{ $t('government.application_queue.table.new_review') }}
                </button>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.application_id') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.type') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.project_name') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.applicant') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.submitted') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.status') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.priority') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.application_queue.table.columns.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr class="hover:bg-gray-50">
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-navy"
                  >
                    PIN-2024-001
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    PIN
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Solar Farm Project Alpha
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    GreenTech Solutions
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    2024-01-15
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                      >{{ $t('government.application_queue.filters.pending_review') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                      >{{ $t('government.application_queue.table.priority.high') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      {{ $t('government.application_queue.table.actions.review') }}
                    </button>
                    <button class="text-electric-blue hover:text-blue-600">
                      {{ $t('government.application_queue.table.actions.details') }}
                    </button>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-navy"
                  >
                    PDD-2024-002
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    PDD
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Wind Energy Initiative
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    EcoWind Ltd
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    2024-01-18
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                      >{{ $t('government.application_queue.filters.under_review') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full"
                      >{{ $t('government.application_queue.table.priority.medium') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      {{ $t('government.application_queue.table.actions.review') }}
                    </button>
                    <button class="text-electric-blue hover:text-blue-600">
                      {{ $t('government.application_queue.table.actions.details') }}
                    </button>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-navy"
                  >
                    AUTH-2024-003
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Authorization
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Forest Conservation Project
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Nature Preserve Inc
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    2024-01-20
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                      >{{ $t('government.application_queue.filters.approved') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                      >{{ $t('government.application_queue.table.priority.low') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      {{ $t('government.application_queue.table.actions.review') }}
                    </button>
                    <button class="text-electric-blue hover:text-blue-600">
                      {{ $t('government.application_queue.table.actions.details') }}
                    </button>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-navy"
                  >
                    PIN-2024-004
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    PIN
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Biogas Recovery System
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    BioEnergy Corp
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    2024-01-22
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full"
                      >{{ $t('government.application_queue.filters.pending_review') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full"
                      >{{ $t('government.application_queue.table.priority.medium') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      {{ $t('government.application_queue.table.actions.review') }}
                    </button>
                    <button class="text-electric-blue hover:text-blue-600">
                      {{ $t('government.application_queue.table.actions.details') }}
                    </button>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-navy"
                  >
                    PDD-2024-005
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    PDD
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    Hydro Power Plant Beta
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    HydroGen Energy
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    2024-01-25
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                      >{{ $t('government.application_queue.filters.rejected') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                      >{{ $t('government.application_queue.table.priority.high') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      {{ $t('government.application_queue.table.actions.reopen') }}
                    </button>
                    <button class="text-electric-blue hover:text-blue-600">
                      {{ $t('government.application_queue.table.actions.details') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            id="pagination"
            class="px-6 py-3 bg-gray-50 border-t border-light-gray"
          >
            <div class="flex items-center justify-between">
              <div class="text-sm text-gray-700">
                {{ $t('government.application_queue.pagination.showing') }} <span class="font-medium">1</span> {{ $t('government.application_queue.pagination.to') }}
                <span class="font-medium">5</span> {{ $t('government.application_queue.pagination.of') }}
                <span class="font-medium">47</span> {{ $t('government.application_queue.pagination.results') }}
              </div>
              <div class="flex space-x-2">
                <button
                  class="px-3 py-1 text-sm border border-light-gray rounded-md hover:bg-white"
                >
                  {{ $t('government.application_queue.pagination.previous') }}
                </button>
                <button
                  class="px-3 py-1 text-sm bg-forest-green text-white rounded-md"
                >
                  1
                </button>
                <button
                  class="px-3 py-1 text-sm border border-light-gray rounded-md hover:bg-white"
                >
                  2
                </button>
                <button
                  class="px-3 py-1 text-sm border border-light-gray rounded-md hover:bg-white"
                >
                  3
                </button>
                <button
                  class="px-3 py-1 text-sm border border-light-gray rounded-md hover:bg-white"
                >
                  {{ $t('government.application_queue.pagination.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
